import React, { useCallback, useEffect, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import QuillResizeImage from 'quill-resize-image';
import 'react-quill/dist/quill.snow.css';
import { useNavigate, useParams } from 'react-router-dom';
import { Axios } from 'services/axios';
import { Loading, Notify } from 'notiflix';
import {
  useForm,
  useFieldArray,
  Controller,
} from 'react-hook-form';

import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Input,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';


/* -------------------------------------------------------------------------- */
/*                              React Quill                                   */
/* -------------------------------------------------------------------------- */

// Formats the toolbar can actually produce — anything else arriving via
// copy/paste (from Word, Google Docs, another site, etc.) is stripped down
// to this set instead of carrying over stray inline styles/classes that
// would just get sanitized away anyway.
// width/height/style: an image's resized size (quill-resize-image) — this matcher
// also runs when an existing case study's HTML is loaded back into the
// editor, so without them a resize would be lost on the next edit.
const ALLOWED_PASTE_FORMATS = [
  'bold', 'italic', 'underline', 'strike', 'link', 'list', 'header', 'color', 'background',
  'width', 'height', 'style',
];

const stripUnknownPasteFormats = (node, delta) => {
  delta.ops.forEach((op) => {
    if (!op.attributes) return;
    Object.keys(op.attributes).forEach((key) => {
      if (!ALLOWED_PASTE_FORMATS.includes(key)) delete op.attributes[key];
    });
  });
  return delta;
};

// Uploads the picked file to the CDN and inserts the returned URL, instead
// of Quill's default behavior of base64-embedding the whole image inline
// in the saved HTML.
// Click an image in the editor to get drag handles + size presets. Dragging
// sets the <img width="…"> attribute (height follows the aspect ratio); the
// 100% / 50% / custom-% buttons and the align buttons set an inline style.
// Quill's own image blot only keeps alt/width/height, so the style was lost
// as soon as the saved HTML was loaded back into the editor - this blot
// keeps it as a format too.
const BaseImage = Quill.import('formats/image');
class StyledImage extends BaseImage {
  static formats(domNode) {
    const formats = super.formats(domNode) || {};
    const style = domNode.getAttribute('style');
    if (style) formats.style = style;
    return formats;
  }

  format(name, value) {
    if (name !== 'style') {
      super.format(name, value);
      return;
    }
    if (value) this.domNode.setAttribute('style', value);
    else this.domNode.removeAttribute('style');
  }
}
Quill.register(StyledImage, true);
Quill.register('modules/resize', QuillResizeImage);

function imageHandler() {
  const quill = this.quill;
  const input = document.createElement('input');
  input.setAttribute('type', 'file');
  input.setAttribute('accept', 'image/*');
  input.click();

  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;

    const range = quill.getSelection(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      Loading.circle('Uploading image...');
      const res = await Axios.post('/shop/rich-text-image', formData, {
        'Content-Type': 'multipart/form-data',
      });
      const url = res?.data?.data?.url;
      if (!url) throw new Error('No URL returned');
      quill.insertEmbed(range.index, 'image', url, 'user');
      quill.setSelection(range.index + 1);
    } catch (error) {
      Notify.failure('Image upload failed. Please try again.');
    } finally {
      Loading.remove();
    }
  };
}

const quillModules = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['image', 'link'],
      ['clean'],
    ],
    handlers: {
      image: imageHandler,
    },
  },
  clipboard: {
    matchVisual: false,
    matchers: [[Node.ELEMENT_NODE, stripUnknownPasteFormats]],
  },
  resize: {
    keepAspectRatio: true,
    locale: {},
  },
};


/* -------------------------------------------------------------------------- */
/*                              Environment                                   */
/* -------------------------------------------------------------------------- */

const IS_PRODUCTION = process.env.REACT_APP_IS_PRODUCTION === '1';
const IS_STAGE = process.env.REACT_APP_IS_STAGE === '1';

const FRONTEND_BASE = IS_PRODUCTION
  ? 'https://tecnogex.com'
  : IS_STAGE
    ? 'https://dev.tecnogex.com'
    : 'http://localhost:5173';


/* -------------------------------------------------------------------------- */
/*                            Image Preview                                   */
/* -------------------------------------------------------------------------- */

const ImagePreview = ({
  src,
  alt = 'Preview',
  height = 60,
}) => {
  if (!src) return null;

  return (
    <Box mt={1} mb={1}>
      <img
        src={src}
        alt={alt}
        style={{
          height,
          objectFit: 'cover',
          borderRadius: 4,
          border: '1px solid #ddd',
        }}
      />
    </Box>
  );
};


// Preview under a file input: the newly picked file, else the saved image URL
const FileOrSavedPreview = ({ files, savedUrl, alt }) => {
  const file = files && files.length ? files[0] : null;
  const [fileUrl, setFileUrl] = useState('');

  useEffect(() => {
    if (!(file instanceof File)) {
      setFileUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  return <ImagePreview src={fileUrl || savedUrl} alt={alt} height={80} />;
};

/* -------------------------------------------------------------------------- */
/*                            Case Study Form                                 */
/* -------------------------------------------------------------------------- */

const CaseStudyForm = () => {
  const { caseStudyId } = useParams();
  const navigate = useNavigate();

  const isEdit = Boolean(caseStudyId);

  const [categoriesList, setCategoriesList] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const [active, setActive] = useState(true);
  // Saved image URLs of the case study being edited, shown under each file
  // input (a newly picked file shows instead until the form is saved)
  const [savedImages, setSavedImages] = useState({});
  // Clear the saved testimonial photo on save
  const [removeTestimonialImage, setRemoveTestimonialImage] = useState(false);
  // Clear the saved success banner image on save
  const [removeSuccessImage, setRemoveSuccessImage] = useState(false);
  // Title of the opened case study, shown in the breadcrumb instead of its id
  const [loadedTitle, setLoadedTitle] = useState('');


  /* ------------------------------------------------------------------------ */
  /*                              Form Setup                                  */
  /* ------------------------------------------------------------------------ */

  const {
    register,
    handleSubmit,
    reset,
    control,
    getValues,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: '',
      description: '',
      read_time: '',
      image: null,
      categoryIds: [],

      hero_title: '',
      hero_subtitle: '',
      hero_description: '',
      hero_image: null,

      vision_title: '',
      vision_description: '',
      vision_link: '',
      vision_image: null,

      // Testimonial on the case study page
      testimonial_badge: '',
      testimonial_content: '',
      testimonial_name: '',
      testimonial_position: '',
      testimonial_company: '',
      testimonial_image: null,

      // Heading above the solutions slider on the case study page
      enterprise_title: '',
      enterprise_description: '',

      // Success banner (bottom CTA) on the case study page
      success_title: '',
      success_description: '',
      success_button_text: '',
      success_button_link: '',
      success_image: null,

      company_logo: null,
      company_industry: '',
      company_size: '',
      company_location: '',
      company_description: '',

      // Cards 1-3 required, 4-5 optional
      stats: [
        {
          value: '',
          title: '',
          description: '',
        },
        {
          value: '',
          title: '',
          description: '',
        },
        {
          value: '',
          title: '',
          description: '',
        },
        {
          value: '',
          title: '',
          description: '',
        },
        {
          value: '',
          title: '',
          description: '',
        },
      ],

      case_study_categories: [],
      similarCaseStudies: [],
      slug: '',
    },
  });


  /* ------------------------------------------------------------------------ */
  /*                           Field Arrays                                   */
  /* ------------------------------------------------------------------------ */

  const { fields: statFields } = useFieldArray({
    control,
    name: 'stats',
  });

  const {
    fields: sectionFields,
    remove: removeSection,
    replace: replaceSection,
  } = useFieldArray({
    control,
    name: 'case_study_categories',
  });


  /* ------------------------------------------------------------------------ */
  /*                       Sync Category Sections                             */
  /* ------------------------------------------------------------------------ */

  const syncSections = useCallback(
    (selectedIds) => {
      const currentSections =
        getValues('case_study_categories') || [];

      const newSections = selectedIds.map((catId) => {
        const cat = categoriesList.find(
          (c) => c.id === catId
        );

        const catName = cat?.name || '';

        const existing = currentSections.find(
          (section) =>
            section.category_name === catName
        );

        return (
          existing || {
            category_name: catName,

            challenge_title: '',
            challenges: '',
            challenge_points_text: '',

            solution_title: '',
            solution: '',
            solution_points_text: '',

            result_title: '',
            result: '',
            result_points_text: '',
          }
        );
      });

      replaceSection(newSections);
    },
    [
      categoriesList,
      getValues,
      replaceSection,
    ]
  );


  /* ------------------------------------------------------------------------ */
  /*                         Fetch Categories                                 */
  /* ------------------------------------------------------------------------ */

  const fetchCategories = useCallback(async () => {
    try {
      const response = await Axios.fetch(
        '/shop/getAllActiveCategories'
      );

      if (response?.data?.data) {
        setCategoriesList(response.data.data);
      }
    } catch (err) {
      console.error(
        'Error fetching categories:',
        err
      );
    }
  }, []);


  /* ------------------------------------------------------------------------ */
  /*                       Fetch Case Studies                                 */
  /* ------------------------------------------------------------------------ */

  const fetchAllCaseStudies = useCallback(async () => {
    try {
      const response = await Axios.fetch(
        '/shop/getAllCaseStudies'
      );

      setCaseStudies(
        response?.data?.data || []
      );
    } catch (err) {
      console.error(
        'Error fetching case studies:',
        err
      );
    }
  }, []);


  /* ------------------------------------------------------------------------ */
  /*                       Fetch Single Case Study                             */
  /* ------------------------------------------------------------------------ */

  const fetchCaseStudy = useCallback(async () => {
    if (!caseStudyId) return;

    Loading.circle('Loading Case Study');

    try {
      const response = await Axios.fetch(
        `/shop/caseStudies/${caseStudyId}`
      );

      const full = response.data.data;
      const details = full.details || {};

      setSavedImages({
        image: full.image || '',
        company_logo: details.company_logo || '',
        vision_image: details.vision_image || '',
        testimonial_image: details.testimonial_image || '',
        success_image: details.success_image || '',
      });
      setRemoveTestimonialImage(false);
      setRemoveSuccessImage(false);
      setLoadedTitle(full.title || '');

      reset({
        title: full.title || '',
        description: full.description || '',
        read_time:
          details.read_time ||
          full.read_time ||
          '',

        categoryIds: (() => {
          if (
            Array.isArray(full.sections) &&
            full.sections.length > 0
          ) {
            const ids = full.sections
              .map(
                (section) =>
                  categoriesList.find(
                    (category) =>
                      category.name ===
                      section.category_name
                  )?.id
              )
              .filter(Boolean);

            if (ids.length > 0) {
              return ids;
            }
          }

          if (details.category_ids) {
            try {
              return typeof details.category_ids ===
                'string'
                ? JSON.parse(
                    details.category_ids
                  )
                : details.category_ids;
            } catch {
              return [];
            }
          }

          return full.categoryId
            ? [full.categoryId]
            : [];
        })(),

        hero_title:
          details.hero_title || '',

        hero_subtitle:
          details.hero_subtitle || '',

        hero_description:
          details.hero_description || '',

        vision_title:
          details.vision_title || '',

        vision_description:
          details.vision_description || '',

        vision_link:
          details.vision_link || '',

        testimonial_badge:
          details.testimonial_badge || '',
        testimonial_content:
          details.testimonial_content || '',
        testimonial_name:
          details.testimonial_name || '',
        testimonial_position:
          details.testimonial_position || '',
        testimonial_company:
          details.testimonial_company || '',

        enterprise_title:
          details.enterprise_title || '',
        enterprise_description:
          details.enterprise_description || '',

        success_title: details.success_title || '',
        success_description: details.success_description || '',
        success_button_text: details.success_button_text || '',
        success_button_link: details.success_button_link || '',

        company_industry:
          details.company_industry || '',

        company_size:
          details.company_size || '',

        company_location:
          details.company_location || '',

        company_description:
          details.company_description || '',

        stats: (() => {
          const base = Array.isArray(full.stats)
            ? full.stats
            : [];

          const empty = {
            value: '',
            title: '',
            description: '',
          };

          return [0, 1, 2, 3, 4].map(
            (index) =>
              base[index] || empty
          );
        })(),

        case_study_categories:
          Array.isArray(full.sections)
            ? full.sections.map((section) => ({
                category_name:
                  section.category_name || '',

                challenge_title:
                  section.challenge_title || '',

                challenges:
                  section.challenges || '',

                challenge_points_text: (() => {
                  const points =
                    section.challenge_points;

                  if (
                    !Array.isArray(points) ||
                    points.length === 0
                  ) {
                    return '';
                  }

                  if (
                    points.length === 1 &&
                    /<[a-z]/i.test(points[0])
                  ) {
                    return points[0];
                  }

                  return points
                    .map(
                      (point) =>
                        `<p>${point}</p>`
                    )
                    .join('');
                })(),

                solution_title:
                  section.solution_title || '',

                solution:
                  section.solution || '',

                solution_points_text: (() => {
                  const points =
                    section.solution_points;

                  if (
                    !Array.isArray(points) ||
                    points.length === 0
                  ) {
                    return '';
                  }

                  if (
                    points.length === 1 &&
                    /<[a-z]/i.test(points[0])
                  ) {
                    return points[0];
                  }

                  return points
                    .map(
                      (point) =>
                        `<p>${point}</p>`
                    )
                    .join('');
                })(),

                result_title:
                  section.result_title || '',

                result:
                  section.result || '',

                result_points_text: (() => {
                  const points =
                    section.result_points;

                  if (
                    !Array.isArray(points) ||
                    points.length === 0
                  ) {
                    return '';
                  }

                  if (
                    points.length === 1 &&
                    /<[a-z]/i.test(points[0])
                  ) {
                    return points[0];
                  }

                  return points
                    .map(
                      (point) =>
                        `<p>${point}</p>`
                    )
                    .join('');
                })(),
              }))
            : [],

        similarCaseStudies:
          full.similarCaseStudies?.map(
            (caseStudy) => caseStudy.id
          ) || [],

        slug: full.slug || '',
      });

      setActive(full.active !== false);
    } catch (err) {
      Notify.failure(
        err.response?.data?.message ||
          'Failed to load case study'
      );
    } finally {
      Loading.remove();
    }
  }, [
    caseStudyId,
    categoriesList,
    reset,
  ]);


  /* ------------------------------------------------------------------------ */
  /*                              Effects                                     */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    fetchCategories();
    fetchAllCaseStudies();
  }, [
    fetchCategories,
    fetchAllCaseStudies,
  ]);

  useEffect(() => {
    if (
      isEdit &&
      categoriesList.length > 0
    ) {
      fetchCaseStudy();
    }
  }, [
    isEdit,
    categoriesList.length,
    fetchCaseStudy,
  ]);


  /* ------------------------------------------------------------------------ */
  /*                              Submit                                      */
  /* ------------------------------------------------------------------------ */

  const onSubmit = async (data) => {
    const statsErrors = [];

    // Cards 4-5 are optional: skipped when empty, all fields once started
    const statFilled = (stat) =>
      Boolean(
        stat.value?.trim() ||
          stat.title?.trim() ||
          stat.description?.trim()
      );

    (data.stats || []).forEach(
      (stat, index) => {
        if (index >= 3 && !statFilled(stat)) return;

        if (!stat.value?.trim()) {
          statsErrors.push(
            `Stat ${index + 1}: "Value" is required.`
          );
        }

        if (!stat.title?.trim()) {
          statsErrors.push(
            `Stat ${index + 1}: "Title" is required.`
          );
        }

        if (!stat.description?.trim()) {
          statsErrors.push(
            `Stat ${index + 1}: "Description" is required.`
          );
        }
      }
    );

    if (statsErrors.length > 0) {
      Notify.failure(
        statsErrors.join('\n')
      );
      return;
    }


    const isQuillEmpty = (value) =>
      !value?.trim() ||
      value.trim() === '<p><br></p>';


    const groupHasAny = (
      section,
      group
    ) => {
      if (group === 'challenge') {
        return (
          !!section.challenge_title?.trim() ||
          !isQuillEmpty(
            section.challenge_points_text
          )
        );
      }

      if (group === 'solution') {
        return (
          !!section.solution_title?.trim() ||
          !isQuillEmpty(
            section.solution_points_text
          )
        );
      }

      if (group === 'result') {
        return (
          !!section.result_title?.trim() ||
          !isQuillEmpty(
            section.result_points_text
          )
        );
      }

      return false;
    };


    const sectionErrors = [];

    (
      data.case_study_categories || []
    ).forEach((section, index) => {
      const name =
        section.category_name ||
        `Section ${index + 1}`;

      const anyFilled =
        groupHasAny(
          section,
          'challenge'
        ) ||
        groupHasAny(
          section,
          'solution'
        ) ||
        groupHasAny(
          section,
          'result'
        );

      if (anyFilled) {
        if (
          !section.challenge_title?.trim()
        ) {
          sectionErrors.push(
            `"${name}" Challenge: Heading is required.`
          );
        }

        if (
          isQuillEmpty(
            section.challenge_points_text
          )
        ) {
          sectionErrors.push(
            `"${name}" Challenge: Points are required.`
          );
        }

        if (
          !section.solution_title?.trim()
        ) {
          sectionErrors.push(
            `"${name}" Solution: Heading is required.`
          );
        }

        if (
          isQuillEmpty(
            section.solution_points_text
          )
        ) {
          sectionErrors.push(
            `"${name}" Solution: Points are required.`
          );
        }

        if (
          !section.result_title?.trim()
        ) {
          sectionErrors.push(
            `"${name}" Result: Heading is required.`
          );
        }

        if (
          isQuillEmpty(
            section.result_points_text
          )
        ) {
          sectionErrors.push(
            `"${name}" Result: Points are required.`
          );
        }
      }
    });


    if (sectionErrors.length > 0) {
      Notify.failure(
        sectionErrors.join('\n')
      );
      return;
    }


    Loading.circle('Saving Case Study');

    const formData = new FormData();

    formData.append(
      'title',
      data.title
    );

    formData.append(
      'slug',
      data.slug
    );

    formData.append(
      'description',
      data.description || ''
    );

    formData.append(
      'categoryId',
      data.categoryIds?.[0] || ''
    );

    formData.append(
      'active',
      active
    );

    formData.append(
      'hero_title',
      data.hero_title || ''
    );

    formData.append(
      'hero_subtitle',
      data.hero_subtitle || ''
    );

    formData.append(
      'hero_description',
      data.hero_description || ''
    );

    formData.append(
      'vision_title',
      data.vision_title || ''
    );

    formData.append(
      'vision_description',
      data.vision_description || ''
    );

    formData.append(
      'vision_link',
      data.vision_link || ''
    );

    // Testimonial (shown on the case study page)
    [
      'testimonial_badge',
      'testimonial_content',
      'testimonial_name',
      'testimonial_position',
      'testimonial_company',
      'enterprise_title',
      'enterprise_description',
      'success_title',
      'success_description',
      'success_button_text',
      'success_button_link',
    ].forEach((key) => formData.append(key, data[key] || ''));

    if (data.testimonial_image?.[0]) {
      formData.append('testimonial_image', data.testimonial_image[0]);
    } else if (removeTestimonialImage) {
      formData.append('remove_testimonial_image', 'true');
    }

    if (data.success_image?.[0]) {
      formData.append('success_image', data.success_image[0]);
    } else if (removeSuccessImage) {
      formData.append('remove_success_image', 'true');
    }

    formData.append(
      'read_time',
      data.read_time || ''
    );

    formData.append(
      'category_ids',
      JSON.stringify(
        data.categoryIds || []
      )
    );

    formData.append(
      'company_industry',
      data.company_industry || ''
    );

    formData.append(
      'company_size',
      data.company_size || ''
    );

    formData.append(
      'company_location',
      data.company_location || ''
    );

    formData.append(
      'company_description',
      data.company_description || ''
    );

    formData.append(
      'stats',
      JSON.stringify(
        (data.stats || []).filter(
          (stat, index) =>
            index < 3 || statFilled(stat)
        )
      )
    );


    const sectionsForJson =
      (
        data.case_study_categories ||
        []
      ).map((section) => ({
        category_name:
          section.category_name || '',

        challenge_title:
          section.challenge_title || '',

        challenges:
          section.challenges || '',

        challenge_points:
          section.challenge_points_text
            ? [section.challenge_points_text]
            : [],

        solution_title:
          section.solution_title || '',

        solution:
          section.solution || '',

        solution_points:
          section.solution_points_text
            ? [section.solution_points_text]
            : [],

        result_title:
          section.result_title || '',

        result:
          section.result || '',

        result_points:
          section.result_points_text
            ? [section.result_points_text]
            : [],
      }));


    formData.append(
      'case_study_categories',
      JSON.stringify(
        sectionsForJson
      )
    );

    formData.append(
      'valuePacks',
      JSON.stringify([])
    );

    formData.append(
      'similarCaseStudies',
      JSON.stringify(
        data.similarCaseStudies || []
      )
    );


    if (data.image?.[0]) {
      formData.append(
        'image',
        data.image[0]
      );
    }

    if (data.hero_image?.[0]) {
      formData.append(
        'hero_image',
        data.hero_image[0]
      );
    }

    if (data.vision_image?.[0]) {
      formData.append(
        'vision_image',
        data.vision_image[0]
      );
    }

    if (data.company_logo?.[0]) {
      formData.append(
        'company_logo',
        data.company_logo[0]
      );
    }


    const url = isEdit
      ? `/shop/addUpdateCaseStudy?caseStudyId=${caseStudyId}`
      : '/shop/addUpdateCaseStudy';


    try {
      const response =
        await Axios.post(
          url,
          formData,
          {
            'Content-type':
              'multipart/form-data',
          }
        );

      Notify.success(
        response.data.message ||
          'Case study saved successfully'
      );

      navigate('/case-studies');
    } catch (error) {
      Notify.failure(
        error.response?.data?.message ||
          'Failed to save case study'
      );
    } finally {
      Loading.remove();
    }
  };


  /* ------------------------------------------------------------------------ */
  /*                                Render                                    */
  /* ------------------------------------------------------------------------ */

  return (
    <DashboardLayout>
      <DashboardNavbar
        routes={[
          { name: 'Case Studies', link: 'case-studies' },
          { name: isEdit ? loadedTitle || 'Edit' : 'New Case Study' },
        ]}
      />

      {/* Header */}
      <MDBox
        mt={4}
        mb={3}
        px={3}
        py={2.5}
        sx={{
          background:
            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '16px',
          boxShadow:
            '0 8px 32px rgba(102,126,234,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <MDBox>
          <MDTypography
            variant="h4"
            fontWeight="bold"
            color="white"
            mb={0.5}
          >
            {isEdit
              ? '✏️ Edit Case Study'
              : '➕ Add Case Study'}
          </MDTypography>

          <MDTypography
            variant="body2"
            sx={{
              color:
                'rgba(255,255,255,0.8)',
            }}
          >
            {isEdit
              ? 'Update the content and settings for this case study'
              : 'Fill in the details to create a new case study'}
          </MDTypography>
        </MDBox>

        <MDButton
          variant="outlined"
          onClick={() =>
            navigate('/case-studies')
          }
          sx={{
            color: '#fff',
            borderColor:
              'rgba(255,255,255,0.5)',
            '&:hover': {
              borderColor: '#fff',
              background:
                'rgba(255,255,255,0.1)',
            },
          }}
        >
          ← Back to Case Studies
        </MDButton>
      </MDBox>


      {/* Form Card */}
      <MDBox
        sx={{
          background: '#fff',
          borderRadius: '16px',
          boxShadow:
            '0 2px 16px rgba(0,0,0,0.06)',
          p: 3,
        }}
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
        >
          <Grid container spacing={3}>

            {/* ---------------------------------------------------------------- */}
            {/* Card Information (case study cards)                             */}
            {/* ---------------------------------------------------------------- */}

            <Grid item xs={12}>
              <Typography
                variant="h6"
                mt={2}
                color="primary"
              >
                Card Information
              </Typography>
              <Divider />
            </Grid>


            <Grid item xs={12}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                }}
                label="Card Title *"
                fullWidth
                {...register('title', {
                  required:
                    'Card Title is required',
                })}
                error={!!errors.title}
                helperText={
                  errors.title?.message
                }
              />
            </Grid>


            <Grid item xs={12}>
              <Controller
                name="slug"
                control={control}
                rules={{
                  required:
                    'Slug is required',
                }}
                render={({ field }) => (
                  <TextField
                    InputLabelProps={{
                      shrink: true,
                    }}
                    label="SEO Slug (URL) *"
                    fullWidth
                    value={
                      field.value || ''
                    }
                    onChange={(event) =>
                      field.onChange(
                        event.target.value
                          .toLowerCase()
                          .replace(
                            /\s+/g,
                            '-'
                          )
                          .replace(
                            /[^a-z0-9-]/g,
                            ''
                          )
                      )
                    }
                    onBlur={
                      field.onBlur
                    }
                    error={!!errors.slug}
                    helperText={
                      errors.slug?.message ||
                      `Preview: ${FRONTEND_BASE}/case-studies/${field.value || '...'}`
                    }
                  />
                )}
              />
            </Grid>


            <Grid item xs={12}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                }}
                label="Card Description *"
                fullWidth
                multiline
                rows={3}
                {...register(
                  'description',
                  {
                    required:
                      'Card Description is required',
                  }
                )}
                error={
                  !!errors.description
                }
                helperText={
                  errors.description?.message
                }
              />
            </Grid>


            <Grid item xs={12} sm={6}>
              <TextField
                label="Read Time"
                InputLabelProps={{ shrink: true }}
                fullWidth
                placeholder="e.g. 5 min read"
                {...register('read_time')}
                helperText="Shown on case study cards"
              />
            </Grid>


            <Grid item xs={12} sm={6}>
              <Controller
                name="categoryIds"
                control={control}
                rules={{
                  required:
                    'At least one category is required',
                }}
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    style={{
                      marginTop: 8,
                    }}
                    error={
                      !!errors.categoryIds
                    }
                  >
                    <Box display="flex" alignItems="center" mb={0.5}>
                      <Typography variant="caption" color="textSecondary">
                        Categories *
                      </Typography>
                      <Tooltip
                        title="Select categories to add their content sections below. Deselect a category to remove its section."
                        placement="top"
                        arrow
                      >
                        <IconButton
                          size="small"
                          aria-label="Category selection help"
                          sx={{ ml: 0.25, p: 0.25 }}
                        >
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Select
                      multiple
                      {...field}
                      value={
                        Array.isArray(
                          field.value
                        )
                          ? field.value
                          : []
                      }
                      onChange={(event) => {
                        field.onChange(
                          event
                        );

                        syncSections(
                          event.target.value
                        );
                      }}
                      input={<OutlinedInput />}
                      style={{
                        minHeight: 43,
                      }}
                    >
                      {categoriesList.map(
                        (category) => (
                          <MenuItem
                            key={
                              category.id
                            }
                            value={
                              category.id
                            }
                          >
                            {category.name}
                          </MenuItem>
                        )
                      )}
                    </Select>

                    {errors.categoryIds && (
                      <Typography
                        variant="caption"
                        color="error"
                      >
                        {
                          errors.categoryIds
                            .message
                        }
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>


            <Grid item xs={12} sm={6}>
              <Typography
                variant="caption"
                color="textSecondary"
              >
                Main Image {!isEdit && '*'}
              </Typography>

              <Input
                type="file"
                fullWidth
                accept="image/*"
                {...register('image', {
                  required: !isEdit,
                })}
              />
              <FileOrSavedPreview
                files={watch('image')}
                savedUrl={savedImages.image}
                alt="Main image"
              />

              {errors.image && (
                <Typography
                  variant="caption"
                  color="error"
                >
                  Image is required
                </Typography>
              )}
            </Grid>


            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={active}
                    onChange={(event) =>
                      setActive(
                        event.target.checked
                      )
                    }
                    color="primary"
                  />
                }
                label="Active"
              />
            </Grid>


            {/* ---------------------------------------------------------------- */}
            {/* Hero Section                                                    */}
            {/* ---------------------------------------------------------------- */}

            <Grid item xs={12}>
              <Typography
                variant="h6"
                mt={2}
                color="primary"
              >
                Hero Section
              </Typography>
              <Divider />
            </Grid>


            <Grid item xs={12}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                }}
                label="Company Name"
                fullWidth
                {...register(
                  'hero_title'
                )}
              />
            </Grid>


            <Grid item xs={12}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                }}
                label="Title"
                fullWidth
                {...register(
                  'hero_subtitle'
                )}
              />
            </Grid>


            <Grid item xs={12}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                }}
                label="Hero Description"
                fullWidth
                multiline
                rows={3}
                {...register(
                  'hero_description'
                )}
              />
            </Grid>


            {/* ---------------------------------------------------------------- */}
            {/* Company Information                                              */}
            {/* ---------------------------------------------------------------- */}

            <Grid item xs={12}>
              <Typography
                variant="h6"
                mt={2}
                color="primary"
              >
                Company Info
              </Typography>
              <Divider />
            </Grid>


            <Grid item xs={12} sm={6}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                }}
                label="Industry"
                fullWidth
                {...register(
                  'company_industry'
                )}
              />
            </Grid>


            <Grid item xs={12} sm={6}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                }}
                label="Partnership"
                fullWidth
                {...register(
                  'company_size'
                )}
              />
            </Grid>


            <Grid item xs={12} sm={6}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                }}
                label="Location"
                fullWidth
                {...register(
                  'company_location'
                )}
              />
            </Grid>


            <Grid item xs={12} sm={6}>
              <Typography
                variant="caption"
                color="textSecondary"
              >
                Company Logo
              </Typography>

              <Input
                type="file"
                fullWidth
                accept="image/*"
                {...register(
                  'company_logo'
                )}
              />
              <FileOrSavedPreview
                files={watch('company_logo')}
                savedUrl={savedImages.company_logo}
                alt="Company logo"
              />
            </Grid>


            <Grid item xs={12}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                }}
                label="Company Description"
                fullWidth
                multiline
                rows={3}
                {...register(
                  'company_description'
                )}
              />
            </Grid>


            {/* ---------------------------------------------------------------- */}
            {/* Vision Section                                                   */}
            {/* ---------------------------------------------------------------- */}

            <Grid item xs={12}>
              <Typography
                variant="h6"
                mt={2}
                color="primary"
              >
                Vision Section
              </Typography>
              <Divider />
            </Grid>


            <Grid item xs={12}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                }}
                label="Vision Title"
                fullWidth
                {...register(
                  'vision_title'
                )}
              />
            </Grid>


            <Grid item xs={12}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                }}
                label="Vision Description"
                fullWidth
                multiline
                rows={3}
                {...register(
                  'vision_description'
                )}
              />
            </Grid>


            <Grid item xs={12}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                }}
                label="Arrow Link URL"
                fullWidth
                placeholder="https://example.com"
                {...register(
                  'vision_link'
                )}
              />
            </Grid>


            <Grid item xs={12}>
              <Typography
                variant="caption"
                color="textSecondary"
              >
                Vision Image
              </Typography>

              <Input
                type="file"
                fullWidth
                accept="image/*"
                {...register(
                  'vision_image'
                )}
              />
              <FileOrSavedPreview
                files={watch('vision_image')}
                savedUrl={savedImages.vision_image}
                alt="Vision image"
              />
            </Grid>


            {/* ---------------------------------------------------------------- */}
            {/* Testimonial (case study page, instead of the CMS one)            */}
            {/* ---------------------------------------------------------------- */}

            <Grid item xs={12}>
              <Typography
                variant="h6"
                mt={2}
                color="primary"
              >
                Testimonial
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
              >
                Shown on this case study&apos;s page. Leave the quote and name
                empty to hide it. HTML allowed.
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                InputLabelProps={{ shrink: true }}
                label="Badge"
                placeholder="Testimonials"
                fullWidth
                {...register('testimonial_badge')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                InputLabelProps={{ shrink: true }}
                label="Quote"
                fullWidth
                multiline
                rows={4}
                {...register('testimonial_content')}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                InputLabelProps={{ shrink: true }}
                label="Name"
                fullWidth
                {...register('testimonial_name')}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                InputLabelProps={{ shrink: true }}
                label="Position"
                fullWidth
                {...register('testimonial_position')}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                InputLabelProps={{ shrink: true }}
                label="Company"
                fullWidth
                {...register('testimonial_company')}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="caption"
                color="textSecondary"
              >
                Photo
              </Typography>

              <Input
                type="file"
                fullWidth
                accept="image/*"
                {...register('testimonial_image')}
              />
              <FileOrSavedPreview
                files={watch('testimonial_image')}
                savedUrl={removeTestimonialImage ? '' : savedImages.testimonial_image}
                alt="Testimonial photo"
              />
              {savedImages.testimonial_image && !removeTestimonialImage && (
                <Button
                  size="small"
                  color="error"
                  onClick={() => setRemoveTestimonialImage(true)}
                >
                  Remove photo
                </Button>
              )}
            </Grid>


            {/* ---------------------------------------------------------------- */}
            {/* Solutions heading (above the solutions slider on the page)       */}
            {/* ---------------------------------------------------------------- */}

            <Grid item xs={12}>
              <Typography
                variant="h6"
                mt={2}
                color="primary"
              >
                Solutions Heading
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
              >
                Heading and description above the solutions slider (e.g.
                &quot;Empowering Enterprise Excellence&quot;). Leave empty to
                use the default text. HTML allowed.
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                InputLabelProps={{ shrink: true }}
                label="Heading"
                placeholder="Empowering Enterprise Excellence"
                fullWidth
                {...register('enterprise_title')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                InputLabelProps={{ shrink: true }}
                label="Description"
                placeholder="Partner with us to power your agency's growth."
                fullWidth
                multiline
                rows={2}
                {...register('enterprise_description')}
              />
            </Grid>


            {/* ---------------------------------------------------------------- */}
            {/* Success banner (bottom CTA on the page)                          */}
            {/* ---------------------------------------------------------------- */}

            <Grid item xs={12}>
              <Typography
                variant="h6"
                mt={2}
                color="primary"
              >
                Success Section
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
              >
                Banner at the bottom of this case study&apos;s page. Any field
                left empty uses the default (CMS) content. HTML allowed.
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                InputLabelProps={{ shrink: true }}
                label="Title"
                fullWidth
                {...register('success_title')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                InputLabelProps={{ shrink: true }}
                label="Description"
                fullWidth
                multiline
                rows={3}
                {...register('success_description')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                InputLabelProps={{ shrink: true }}
                label="Button Text"
                fullWidth
                {...register('success_button_text')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                InputLabelProps={{ shrink: true }}
                label="Button URL"
                placeholder="/contact-us or https://..."
                helperText="Leave empty to open the enquiry form"
                fullWidth
                {...register('success_button_link')}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="caption"
                color="textSecondary"
              >
                Background Image
              </Typography>

              <Input
                type="file"
                fullWidth
                accept="image/*"
                {...register('success_image')}
              />
              <FileOrSavedPreview
                files={watch('success_image')}
                savedUrl={removeSuccessImage ? '' : savedImages.success_image}
                alt="Success background"
              />
              {savedImages.success_image && !removeSuccessImage && (
                <Button
                  size="small"
                  color="error"
                  onClick={() => setRemoveSuccessImage(true)}
                >
                  Remove image
                </Button>
              )}
            </Grid>


            {/* ---------------------------------------------------------------- */}
            {/* Stats                                                            */}
            {/* ---------------------------------------------------------------- */}

            <Grid item xs={12}>
              <Typography
                variant="h6"
                mt={2}
                color="primary"
              >
                Stats
              </Typography>

              <Divider
                sx={{
                  mb: 2,
                }}
              />
            </Grid>


            <Grid item xs={12}>
              {statFields.map(
                (item, index) => (
                  <Box
                    key={item.id}
                    p={2}
                    mb={2}
                    border="1px solid #eee"
                    borderRadius="8px"
                  >
                    <Grid
                      container
                      spacing={2}
                    >

                      <Grid item xs={12}>
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          color="textSecondary"
                        >
                          Stat {index + 1}
                          {index >= 3
                            ? ' (optional)'
                            : ''}
                        </Typography>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        sm={4}
                      >
                        <TextField
                          InputLabelProps={{
                            shrink: true,
                          }}
                          label="Value (e.g. 300%)"
                          fullWidth
                          {...register(
                            `stats.${index}.value`
                          )}
                        />
                      </Grid>


                      <Grid
                        item
                        xs={12}
                        sm={8}
                      >
                        <TextField
                          InputLabelProps={{
                            shrink: true,
                          }}
                          label="Title"
                          fullWidth
                          {...register(
                            `stats.${index}.title`
                          )}
                        />
                      </Grid>


                      <Grid
                        item
                        xs={12}
                      >
                        <TextField
                          InputLabelProps={{
                            shrink: true,
                          }}
                          label="Description"
                          fullWidth
                          multiline
                          rows={2}
                          {...register(
                            `stats.${index}.description`
                          )}
                        />
                      </Grid>

                    </Grid>
                  </Box>
                )
              )}
            </Grid>


            {/* ---------------------------------------------------------------- */}
            {/* Service Sections                                                 */}
            {/* ---------------------------------------------------------------- */}

            <Grid item xs={12}>
              <Typography
                variant="h6"
                mt={2}
                color="primary"
              >
                Service Sections
              </Typography>

              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                mb={1}
              >
                Auto-generated from selected
                categories above.
              </Typography>

              <Divider
                sx={{
                  mb: 2,
                }}
              />
            </Grid>


            <Grid item xs={12}>

              {sectionFields.length === 0 && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                >
                  No sections yet. Select
                  categories above.
                </Typography>
              )}


              {sectionFields.map(
                (item, index) => (
                  <Box
                    key={item.id}
                    p={2}
                    mb={2}
                    border="1px solid #eee"
                    borderRadius="8px"
                    position="relative"
                  >

                    <IconButton
                      size="small"
                      color="error"
                      sx={{
                        position:
                          'absolute',
                        top: 5,
                        right: 5,
                      }}
                      onClick={() =>
                        removeSection(
                          index
                        )
                      }
                    >
                      <DeleteIcon />
                    </IconButton>


                    <Grid
                      container
                      spacing={2}
                    >

                      {/* Section Name */}
                      <Grid
                        item
                        xs={12}
                      >
                        <TextField
                          InputLabelProps={{
                            shrink: true,
                          }}
                          label="Service Section Name"
                          fullWidth
                          {...register(
                            `case_study_categories.${index}.category_name`
                          )}
                          InputProps={{
                            readOnly: true,
                          }}
                          sx={{
                            '& .MuiInputBase-input':
                              {
                                color:
                                  '#1976d2',
                                fontWeight:
                                  600,
                              },
                          }}
                        />
                      </Grid>


                      {/* Challenge */}
                      <Grid
                        item
                        xs={12}
                      >
                        <Typography
                          variant="caption"
                          color="primary"
                          fontWeight="bold"
                        >
                          Challenge
                        </Typography>
                      </Grid>


                      <Grid
                        item
                        xs={12}
                      >
                        <TextField
                          InputLabelProps={{
                            shrink: true,
                          }}
                          label="Challenge Heading"
                          fullWidth
                          {...register(
                            `case_study_categories.${index}.challenge_title`
                          )}
                        />
                      </Grid>


                      <Grid
                        item
                        xs={12}
                      >
                        <Controller
                          name={`case_study_categories.${index}.challenge_points_text`}
                          control={control}
                          render={({
                            field,
                          }) => (
                            <ReactQuill
                              theme="snow"
                              modules={
                                quillModules
                              }
                              value={
                                field.value ||
                                ''
                              }
                              onChange={
                                field.onChange
                              }
                              style={{
                                marginBottom:
                                  '40px',
                              }}
                            />
                          )}
                        />
                      </Grid>


                      {/* Solution */}
                      <Grid
                        item
                        xs={12}
                      >
                        <Typography
                          variant="caption"
                          color="primary"
                          fontWeight="bold"
                        >
                          Solution
                        </Typography>
                      </Grid>


                      <Grid
                        item
                        xs={12}
                      >
                        <TextField
                          InputLabelProps={{
                            shrink: true,
                          }}
                          label="Solution Heading"
                          fullWidth
                          {...register(
                            `case_study_categories.${index}.solution_title`
                          )}
                        />
                      </Grid>


                      <Grid
                        item
                        xs={12}
                      >
                        <Controller
                          name={`case_study_categories.${index}.solution_points_text`}
                          control={control}
                          render={({
                            field,
                          }) => (
                            <ReactQuill
                              theme="snow"
                              modules={
                                quillModules
                              }
                              value={
                                field.value ||
                                ''
                              }
                              onChange={
                                field.onChange
                              }
                              style={{
                                marginBottom:
                                  '40px',
                              }}
                            />
                          )}
                        />
                      </Grid>


                      {/* Result */}
                      <Grid
                        item
                        xs={12}
                      >
                        <Typography
                          variant="caption"
                          color="primary"
                          fontWeight="bold"
                        >
                          Result
                        </Typography>
                      </Grid>


                      <Grid
                        item
                        xs={12}
                      >
                        <TextField
                          InputLabelProps={{
                            shrink: true,
                          }}
                          label="Result Heading"
                          fullWidth
                          {...register(
                            `case_study_categories.${index}.result_title`
                          )}
                        />
                      </Grid>


                      <Grid
                        item
                        xs={12}
                      >
                        <Controller
                          name={`case_study_categories.${index}.result_points_text`}
                          control={control}
                          render={({
                            field,
                          }) => (
                            <ReactQuill
                              theme="snow"
                              modules={
                                quillModules
                              }
                              value={
                                field.value ||
                                ''
                              }
                              onChange={
                                field.onChange
                              }
                              style={{
                                marginBottom:
                                  '40px',
                              }}
                            />
                          )}
                        />
                      </Grid>

                    </Grid>
                  </Box>
                )
              )}

            </Grid>


            {/* ---------------------------------------------------------------- */}
            {/* Similar Case Studies                                             */}
            {/* ---------------------------------------------------------------- */}

            <Grid item xs={12}>
              <Typography
                variant="h6"
                mt={2}
                color="primary"
              >
                Similar Case Studies
              </Typography>

              <Divider />
            </Grid>


            <Grid item xs={12}>
              <Controller
                name="similarCaseStudies"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>

                    <InputLabel>
                      Similar Case Studies
                    </InputLabel>

                    <Select
                      multiple
                      input={
                        <OutlinedInput label="Similar Case Studies" />
                      }
                      {...field}
                      value={
                        Array.isArray(
                          field.value
                        )
                          ? field.value
                          : []
                      }
                      style={{
                        minHeight: 43,
                      }}
                    >
                      {(() => {
                        const selectedCatIds =
                          watch(
                            'categoryIds'
                          ) || [];

                        return caseStudies
                          .filter(
                            (caseStudy) =>
                              caseStudy.id !==
                                caseStudyId &&
                              selectedCatIds.length >
                                0 &&
                              selectedCatIds.includes(
                                caseStudy.categoryId
                              )
                          )
                          .map(
                            (
                              caseStudy
                            ) => (
                              <MenuItem
                                key={
                                  caseStudy.id
                                }
                                value={
                                  caseStudy.id
                                }
                              >
                                {
                                  caseStudy.title
                                }
                              </MenuItem>
                            )
                          );
                      })()}
                    </Select>

                  </FormControl>
                )}
              />
            </Grid>

          </Grid>


          {/* ------------------------------------------------------------------ */}
          {/* Form Actions                                                       */}
          {/* ------------------------------------------------------------------ */}

          <MDBox
            display="flex"
            justifyContent="flex-end"
            gap={2}
            mt={3}
          >
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={() =>
                navigate(
                  '/case-studies'
                )
              }
            >
              Cancel
            </MDButton>

            <MDButton
              type="submit"
              variant="gradient"
              color="info"
              sx={{
                px: 4,
              }}
            >
              {isEdit
                ? '💾 Update Case Study'
                : '✅ Create Case Study'}
            </MDButton>
          </MDBox>

        </form>
      </MDBox>

    </DashboardLayout>
  );
};

export default CaseStudyForm;
